import { flow, runInAction, autorun, reaction, decorate, observable, computed, action, configure } from 'mobx';
import { createHttpLink, ApolloClient, from } from '@apollo/client';
import * as client from '@apollo/client';
export { client as ApolloClient };
import { IntrospectionFragmentMatcher, InMemoryCache } from 'apollo-cache-inmemory';
import { onError } from '@apollo/client/link/error';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(source, true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(source).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

const DEFAULT_STORAGE_PREFIX = '@social-native/snpkg-client-graphql-client';
const MISSING_TOKEN_ERROR = 'Missing accessToken for the client. You will need to set it using `setAccessToken` before proceeding';
const DEFAULT_GQL_CLIENT_CONFIG = {
  accessToken: null,
  uri: null,
  enablePersistance: false,
  accessTokenStorageKey: `${DEFAULT_STORAGE_PREFIX}/access_token`,
  uriStorageKey: `${DEFAULT_STORAGE_PREFIX}/uri`,
  headers: {}
};

class GqlClient {
  constructor(config) {
    _defineProperty(this, "userProvidedHeaders", void 0);

    _defineProperty(this, "accessTokenStorageKey", void 0);

    _defineProperty(this, "uriStorageKey", void 0);

    _defineProperty(this, "uri", void 0);

    _defineProperty(this, "accessToken", void 0);

    _defineProperty(this, "isPersistanceEnabled", void 0);

    _defineProperty(this, "customLinks", void 0);

    _defineProperty(this, "client", void 0);

    _defineProperty(this, "createClient", flow(function* () {
      try {
        const {
          data: introspectionQueryResultData
        } = yield this.introspectTypes();
        const fragmentMatcher = yield new IntrospectionFragmentMatcher({
          introspectionQueryResultData
        });
        const httpLink = createHttpLink(this.httpLinkOptions);
        this.client = yield new ApolloClient({
          link: from([...this.customLinks, httpLink]),
          cache: new InMemoryCache({
            fragmentMatcher
          })
        });
        return this.client;
      } catch (err) {
        if (err === MISSING_TOKEN_ERROR) {
          // tslint:disable-next-line:no-console
          console.debug(MISSING_TOKEN_ERROR);
        } else {
          throw err;
        }
      }
    }));

    _defineProperty(this, "query", async queryOptions => {
      if (!this.client) {
        throw new Error('Unable to run query. The client has not yet been created. You will need to create it using `createClient` before proceeding.');
      }

      return this.client.query(queryOptions);
    });

    _defineProperty(this, "mutate", async mutationOptions => {
      if (!this.client) {
        throw new Error('Unable to run mutation. The client has not yet been created. You will need to create it using `createClient` before proceeding.');
      }

      return this.client.mutate(mutationOptions);
    });

    _defineProperty(this, "introspectTypes", flow(function* () {
      if (!this.uri) {
        throw new Error('Missing URI for the client. You will need to set it using `setUri` before proceeding`');
      }

      const result = yield fetch(this.uri, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // @ts-ignore
          'Authorization': this.headers.Authorization,
          'Access-Control-Allow-Origin': '*'
        },
        body: JSON.stringify({
          variables: {},
          query: `
                    {
                        __schema {
                            types {
                                kind
                                name
                                possibleTypes {
                                    name
                                }
                            }
                        }
                    }
                `
        })
      });
      const types = yield result.json();
      return types;
    }));

    _defineProperty(this, "setAccessToken", accessToken => {
      this.accessToken = accessToken;
    });

    _defineProperty(this, "setUri", uri => {
      this.uri = uri;
    });

    _defineProperty(this, "togglePersistance", () => {
      this.isPersistanceEnabled = !this.isPersistanceEnabled;
    });

    const c = _objectSpread2({}, DEFAULT_GQL_CLIENT_CONFIG, {}, config);

    runInAction(() => {
      this.accessToken = c.accessToken;
      this.uri = c.uri;
      this.isPersistanceEnabled = c.enablePersistance;
      this.accessTokenStorageKey = c.accessTokenStorageKey;
      this.uriStorageKey = c.uriStorageKey;
      this.userProvidedHeaders = c.headers;
      this.customLinks = c.customLinks || [];
      this.introspectTypes = this.introspectTypes.bind(this);

      if (this.isPersistanceEnabled) {
        // tslint:disable-next-line
        if (c.uri == null) {
          this.uri = window.localStorage.getItem(this.uriStorageKey);
        } // tslint:disable-next-line


        if (c.accessToken == null) {
          this.accessToken = window.localStorage.getItem(this.accessTokenStorageKey);
        }
      }
    });
    autorun(() => {
      if (this.accessToken && this.isPersistanceEnabled) {
        window.localStorage.setItem(this.accessTokenStorageKey, this.accessToken);
      }
    });
    autorun(() => {
      if (this.uri && this.isPersistanceEnabled) {
        window.localStorage.setItem(this.uriStorageKey, this.uri);
      }
    });
    reaction(() => this.uri && this.accessToken, () => this.createClient());
  }

  get httpLinkOptions() {
    if (!this.uri) {
      throw new Error('Missing URI for the client. You will need to set it using `setUri` before proceeding`');
    }

    return {
      uri: this.uri,
      headers: this.headers
    };
  }

  clearPersistedStorage() {
    localStorage.removeItem(this.accessTokenStorageKey);
    localStorage.removeItem(this.uriStorageKey);
  }

  get headers() {
    if (!this.accessToken) {
      throw MISSING_TOKEN_ERROR;
    }

    return _objectSpread2({
      Authorization: `Bearer ${this.accessToken}`
    }, this.userProvidedHeaders);
  } // tslint:disable-line


}

decorate(GqlClient, {
  client: observable,
  uri: observable,
  accessToken: observable,
  isPersistanceEnabled: observable,
  headers: computed,
  httpLinkOptions: computed,
  setAccessToken: action,
  setUri: action,
  togglePersistance: action
});

function createAuthLink(onUnauthenticated) {
  return onError(({
    networkError
  }) => {
    if (isServerError(networkError) && ['401', '403'].includes(networkError.statusCode.toString())) {
      onUnauthenticated();
    }
  });
}

function isServerError(error) {
  if (!error) {
    return false;
  }

  if (!!error.statusCode) {
    return true;
  }

  return false;
}

configure({
  enforceActions: 'always'
});

export { GqlClient, createAuthLink };



//////////////////
// WEBPACK FOOTER
// /root/social-native/socialnative-web/node_modules/@social-native/snpkg-client-graphql-client/dist/index.es.js
// module id = 1913
// module chunks = 69